import type { TaskStatus } from "../data/types";

const LABELS: Record<TaskStatus, string> = {
  done: "Done",
  failed: "Failed",
  "needs-input": "Needs Input",
  pending: "Pending",
  provisioning: "Provisioning",
  review: "In Review",
  running: "In Progress",
};

export const statusLabel = (s: TaskStatus): string => LABELS[s];

/**
 * An elapsed duration, "820ms" / "1.2s". An unmeasured duration renders as
 * nothing rather than an em-dash: the placeholder reads like a measured value,
 * and a call still in flight already says so through its status (chat.md
 * §Tool-call cards).
 */
export const durationLabel = (ms: number | null): string =>
  ms == null ? "" : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;

/** Relative "6m ago" style timestamp from an epoch-millis value. */
export const relTime = (ms: number): string => {
  const d = Math.max(0, Date.now() - ms);
  const m = Math.round(d / 60000);
  if (m < 1) {
    return "just now";
  }
  if (m < 60) {
    return `${m}m ago`;
  }
  const h = Math.round(m / 60);
  if (h < 24) {
    return `${h}h ago`;
  }
  return `${Math.round(h / 24)}d ago`;
};

/**
 * A time on another zone's clock, "Mon, Oct 12, 09:00": 24-hour, so it reads
 * like a routine's `schedule_description` beside it.
 */
export const zonedTime = (iso: string, timeZone: string): string =>
  new Date(iso).toLocaleString("en-US", {
    day: "numeric",
    hour: "2-digit",
    hourCycle: "h23",
    minute: "2-digit",
    month: "short",
    timeZone,
    weekday: "short",
  });
